// Calendar i18n — flat messages consumed by CalendarView and composer.

import { createContext, useContext, type ReactNode } from 'react';
import type { CalendarEventStatus } from './types.js';
import {
  compareDay,
  formatDayShort,
  type PlainDate,
} from './utils/dateUtils.js';
import { defaultCalendarMessages } from './i18n-defaults.js';

export { defaultCalendarMessages } from './i18n-defaults.js';

export interface CalendarMessages {
  // ---- Top-level chrome ----------------------------------------------------
  readonly title: string;
  readonly controlsAriaLabel: string;
  readonly viewSwitcherAriaLabel: string;
  readonly eventsCountOne: string;
  readonly eventsCountOther: string;
  readonly eventsCountZero: string;
  readonly newEvent: string;

  // ---- View labels ---------------------------------------------------------
  readonly viewMonth: string;
  readonly viewWeek: string;
  readonly viewDay: string;
  readonly viewAgenda: string;

  readonly icsExport: string;

  // ---- CalendarHeader (prev / label / next / today) ------------------------
  readonly prev: string;
  readonly next: string;
  readonly today: string;

  // ---- FilterBar / MoreFiltersButton --------------------------------------
  readonly filtersAriaLabel: string;
  readonly allKinds: string;
  readonly filtersTrigger: string;
  readonly filtersTriggerActive: string;
  readonly filtersPanelTitle: string;
  readonly filterSectionGroups: string;
  readonly filterSectionStatus: string;
  readonly resetFilters: string;
  readonly allStatuses: string;
  readonly statusFilterAriaLabel: string;

  // ---- Status labels (drive StatusBadge + filter rows) --------------------
  readonly statusScheduled: string;
  readonly statusLive: string;
  readonly statusPast: string;
  readonly statusCancelled: string;

  // ---- MonthView / MonthViewMobile ----------------------------------------
  readonly cellOverflow: string;
  readonly railAriaLabel: string;
  readonly railEyebrow: string;
  readonly emptyDayTitle: string;
  readonly emptyDayDescription: string;
  readonly dayDetailAriaLabel: string;
  readonly addEventOnDay: string;
  /** Empty-state title used by mobile day detail + agenda. */
  readonly nothingScheduled: string;
  readonly nothingScheduledHint: string;
  readonly addFirstOfDay: string;
  readonly pickAnotherDay: string;
  /** Empty-state title in the DayView when the day has no events. */
  readonly quietDay: string;
  /** DayView empty-state body copy when create is wired. */
  readonly dayDragHint: string;
  /** DayView empty-state body copy when create is NOT wired. */
  readonly dayNoScheduled: string;
  /** DayView rail "At a glance" eyebrow. */
  readonly atAGlance: string;
  /** DayView rail event-count copy — `{count}` is replaced. */
  readonly dayEventsScheduledOne: string;
  readonly dayEventsScheduledOther: string;
  /** WeekView rail aria-label. */
  readonly weekSummaryAriaLabel: string;
  /** DayView rail aria-label. */
  readonly daySummaryAriaLabel: string;
  /** WeekView rail "This week" eyebrow. */
  readonly thisWeek: string;
  /** "Coming up" eyebrow for the upcoming events rail card. */
  readonly comingUp: string;
  /** Aria label for the upcoming-events list in WeekView's rail. */
  readonly upcomingListAriaLabel: string;

  // ---- AgendaView --------------------------------------------------------
  readonly agendaAriaLabel: string;
  readonly agendaNextUp: string;
  readonly agendaThisView: string;
  readonly agendaByKind: string;
  readonly agendaJumpToWeek: string;
  readonly agendaWeekOf: string;
  readonly agendaUpcomingStat: string;
  readonly agendaPastStat: string;
  readonly agendaWeeksStat: string;

  // ---- EventDetailSheet (mobile) ------------------------------------------
  readonly sheetAriaLabel: string;
  readonly sheetDate: string;
  readonly sheetTime: string;
  readonly sheetGroup: string;
  readonly sheetAlsoIn: string;
  readonly sheetLocation: string;
  readonly sheetNotes: string;
  readonly sheetOpen: string;
  /** Edit affordance on the mobile event detail sheet. */
  readonly sheetEdit: string;
  /** Delete affordance on the mobile event detail sheet. */
  readonly sheetDelete: string;

  // ---- relativeDayLabel — used by AgendaView / EventCard ------------------
  readonly relativeToday: string;
  readonly relativeTomorrow: string;
  readonly relativeYesterday: string;
  /** "In N days" — `{count}` is replaced. */
  readonly relativeInDays: string;
  /** "N days ago" — `{count}` is replaced. */
  readonly relativeDaysAgo: string;

  // ---- EventComposer ------------------------------------------------------
  readonly composerNewTitle: string;
  readonly composerEditTitle: string;
  readonly composerNewSubtitle: string;
  readonly composerEditSubtitle: string;
  readonly composerModeAriaLabel: string;
  readonly composerModeSingle: string;
  readonly composerModeSingleSub: string;
  readonly composerModeBatch: string;
  readonly composerModeBatchSub: string;
  readonly composerModeAi: string;
  readonly composerModeAiSub: string;
  readonly composerSectionKind: string;
  readonly composerSectionWhat: string;
  readonly composerSectionWhen: string;
  readonly composerSectionAudience: string;
  readonly composerSectionLocation: string;
  readonly composerSectionRepeat: string;
  readonly composerSectionReminder: string;
  readonly composerSectionNote: string;
  readonly composerSectionPreview: string;
  readonly composerSectionPaste: string;
  readonly composerSectionPasteAi: string;
  readonly composerSectionSuggested: string;
  readonly composerEventKindLabel: string;
  readonly composerTitleLabel: string;
  readonly composerLinkedLabel: string;
  readonly composerDateLabel: string;
  readonly composerTimeLabel: string;
  readonly composerDurationLabel: string;
  readonly composerPrimaryGroupLabel: string;
  /** Sentinel option in the primary-group Select that scopes the event to
   *  the creator only — no group attached. */
  readonly composerPrimaryGroupNone: string;
  readonly composerCrossPostLabel: string;
  readonly composerLocationTypeLabel: string;
  readonly composerLocationNone: string;
  readonly composerLocationOnline: string;
  readonly composerLocationInPerson: string;
  readonly composerLocationCustom: string;
  readonly composerMeetingProviderLabel: string;
  readonly composerMeetingDetailLabel: string;
  readonly composerMeetingDetailPlaceholder: string;
  readonly composerLocationTextLabel: string;
  readonly composerLocationInPersonPlaceholder: string;
  readonly composerLocationCustomPlaceholder: string;
  readonly meetingProviderTencent: string;
  readonly meetingProviderZoom: string;
  readonly meetingProviderGoogleMeet: string;
  readonly meetingProviderTeams: string;
  readonly meetingProviderOther: string;
  readonly composerRepeatLabel: string;
  readonly composerRepeatFrequencyLabel: string;
  readonly composerRepeatUntilLabel: string;
  readonly composerRepeatUntilPlaceholder: string;
  readonly composerReminderLabel: string;
  readonly composerDefaultGroupLabel: string;
  readonly composerDefaultReminderLabel: string;
  readonly composerTitlePlaceholders: Readonly<Record<string, string>>;
  readonly composerDatePlaceholder: string;
  readonly composerDatePickerClear: string;
  readonly composerDatePickerDialogLabel: string;
  readonly composerLinkedNone: string;
  readonly composerNotePlaceholder: string;
  readonly composerBatchPlaceholder: string;
  readonly composerAiPlaceholder: string;
  readonly composerDurationAllDay: string;
  /** "{count} minutes" / "{count} hours" templates. */
  readonly composerDurationMinutes: string;
  readonly composerDurationOneHour: string;
  readonly composerDurationOneHalfHour: string;
  readonly composerDurationTwoHours: string;
  readonly composerRepeatDaily: string;
  readonly composerRepeatWeekly: string;
  readonly composerRepeatBiweekly: string;
  readonly composerRepeatMonthly: string;
  readonly composerReminderNone: string;
  readonly composerReminderByKind: string;
  readonly composerReminderMinutes: string;
  readonly composerReminderOneHour: string;
  readonly composerReminderOneDay: string;
  readonly composerSave: string;
  readonly composerAdd: string;
  readonly composerCancel: string;
  readonly composerDelete: string;
  readonly composerAddEventsZero: string;
  /** "Add {count} event" / "Add {count} events" templates. */
  readonly composerAddEventsOne: string;
  readonly composerAddEventsOther: string;
  readonly composerSkip: string;
  readonly composerRestore: string;
  readonly composerExtract: string;
  readonly composerReading: string;
  readonly composerReExtract: string;
  readonly composerBatchClear: string;
  readonly composerBatchParsedTag: string;
  readonly composerBatchUnclearTag: string;
  readonly composerBatchEmptyTitle: string;
  readonly composerBatchEmptyDesc: string;
  readonly composerAiLoadingTitle: string;
  readonly composerAiLoadingDesc: string;
  readonly composerAiPromptTitle: string;
  readonly composerAiPromptDesc: string;
  readonly composerAiEmptyTitle: string;
  readonly composerAiEmptyDesc: string;
  readonly composerAiRepeatsTag: string;
  readonly composerAiPoweredTag: string;
  readonly composerAiFallbackNote: string;
  readonly composerErrorTitleRequired: string;
  readonly composerErrorDateRequired: string;
  readonly composerErrorRepeatUntilBeforeStart: string;
  readonly composerErrorDuplicateEvent: string;
  readonly composerCrossPostEmpty: string;
  readonly composerActionsAriaLabel: string;
  readonly composerParsedListAriaLabel: string;
  readonly composerSuggestedListAriaLabel: string;
  readonly composerRelatedEventsTitle: string;
  readonly composerRelatedEventsExactTitle: string;
  readonly composerRelatedEventsHint: string;

  // ---- Event cards / context menus ---------------------------------------
  readonly eventCrossPostedTo: string;
  readonly eventRepeatsLabel: string;
  readonly monthCellAriaLabel: string;
  readonly dayEventsBadgeLabel: string;
  readonly eventActionsAriaLabel: string;
  readonly eventMenuOpen: string;
  readonly eventMenuEdit: string;
  readonly eventMenuDuplicate: string;
  readonly eventMenuMoveToToday: string;
  readonly eventMenuUncancel: string;
  readonly eventMenuCancel: string;
  readonly eventMenuDelete: string;

  // ---- Scheduling primitives ---------------------------------------------
  readonly schedulingBookSlotTitle: string;
  readonly schedulingConfirmBooking: string;
  readonly schedulingNoSlotSelected: string;
  readonly schedulingBookingDoneTitle: string;
  readonly schedulingBookingDoneBody: string;
  readonly schedulingDate: string;
  readonly schedulingTime: string;
  readonly schedulingWhere: string;
  readonly schedulingLength: string;
  readonly schedulingLengthMinutes: string;
  readonly schedulingTopicLabel: string;
  readonly schedulingTopicPlaceholder: string;
  readonly schedulingSlotOpen: string;
  readonly schedulingAvailabilityOn: string;
  readonly schedulingWithPeople: string;
  readonly schedulingSlotsOpen: string;
  readonly schedulingSeats: string;
  readonly schedulingCapacityText: string;
  readonly schedulingFullWaitlist: string;
  readonly schedulingCapacityTaken: string;
  readonly schedulingWithHost: string;
  readonly schedulingJoining: string;
  readonly schedulingAttendees: string;
  readonly schedulingJoinWaitlist: string;
  readonly schedulingSaveMySeat: string;
  readonly schedulingRecurringFallback: string;
  readonly schedulingRecurringWithHost: string;
  readonly schedulingSubscribed: string;
  readonly schedulingSeatsPerSession: string;
  readonly schedulingFollow: string;
  readonly schedulingBack: string;
  readonly schedulingOfficeHours: string;
  readonly schedulingSessionLength: string;
  readonly schedulingAvailableSlots: string;
  readonly schedulingOpenSlots: string;
  readonly schedulingFullyBooked: string;
  readonly schedulingGroupSessions: string;
  readonly schedulingGroupSessionsSubtitle: string;
  readonly schedulingSeries: string;
  readonly schedulingSeriesSubtitle: string;
  readonly schedulingSessions: string;
  readonly schedulingSubscribeToSeries: string;
  readonly schedulingRecurringAvailability: string;
  readonly schedulingRecurringAvailabilitySubtitle: string;
}

const CalendarI18nContext =
  createContext<CalendarMessages>(defaultCalendarMessages);

export interface CalendarI18nProviderProps {
  readonly value: CalendarMessages;
  readonly children: ReactNode;
}

/** Wraps a calendar subtree with localised messages. CalendarView mounts
 *  one of these automatically once you pass it `messages`; consumers
 *  rendering MonthView etc. directly can mount their own. */
export function CalendarI18nProvider(
  props: CalendarI18nProviderProps,
): ReactNode {
  return (
    <CalendarI18nContext.Provider value={props.value}>
      {props.children}
    </CalendarI18nContext.Provider>
  );
}

/** Read the active `CalendarMessages`. Returns the English defaults
 *  when no provider is mounted, so component tests / Storybook
 *  consumers don't have to wire one. */
export function useCalendarMessages(): CalendarMessages {
  return useContext(CalendarI18nContext);
}

/** Replace `{name}` placeholders in a message template. The template
 *  language is intentionally tiny — keys map directly to entries on
 *  `vars`, no nested paths, no format specifiers. */
export function formatMessage(
  template: string,
  vars: Record<string, string | number>,
): string {
  let out = template;
  for (const [k, v] of Object.entries(vars)) {
    out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
  }
  return out;
}

/** Pluralise an `{count} event(s)` style counter from the three slots
 *  on `CalendarMessages`. Mirrors i18next's `_zero`/`_one`/`_other`
 *  convention but doesn't take a dependency on it — keeps the package
 *  consumer-agnostic. */
export function formatEventCount(
  messages: CalendarMessages,
  count: number,
): string {
  if (count === 0) return messages.eventsCountZero;
  if (count === 1) return formatMessage(messages.eventsCountOne, { count });
  return formatMessage(messages.eventsCountOther, { count });
}

/** Resolve a status to its localised label. Used by StatusBadge,
 *  StatusFilter, MoreFiltersButton, and anywhere else that needs to
 *  render the user-facing label for a CalendarEventStatus. */
export function resolveStatusLabel(
  messages: CalendarMessages,
  status: CalendarEventStatus,
): string {
  switch (status) {
    case 'scheduled':
      return messages.statusScheduled;
    case 'live':
      return messages.statusLive;
    case 'past':
      return messages.statusPast;
    case 'cancelled':
      return messages.statusCancelled;
  }
}

/** Localised counterpart to `relativeDayLabel` from `utils/dateUtils`.
 *  Folds Today / Tomorrow / Yesterday / In N days / N days ago into
 *  the translation surface; distant dates fall back to the locale's
 *  short month-day format (Intl, locale-aware out of the box). */
export function relativeDayLabelLocalized(
  target: PlainDate,
  today: PlainDate,
  messages: CalendarMessages,
  locale?: string,
): string {
  const diff = compareDay(target, today);
  if (diff === 0) return messages.relativeToday;
  if (diff === 1) return messages.relativeTomorrow;
  if (diff === -1) return messages.relativeYesterday;
  if (diff > 1 && diff < 7) return formatMessage(messages.relativeInDays, { count: diff });
  if (diff < -1 && diff > -7) return formatMessage(messages.relativeDaysAgo, { count: -diff });
  return formatDayShort(target, locale);
}
